import { NarrativeMiniPriceChart } from '@/components/MiniPriceChart/components/MiniPriceChart/NarrativeMiniPriceChart';
import { MiniPriceChartDefinitions } from '@/components/MiniPriceChart/components/MiniPriceChartDefinitions/MiniPriceChartDefinitions';
import { CURRENT_PERIOD } from '@/pages/CompanyReport/components/Overview/components/Header/components/PriceAnalyticsCard/constants';
import { formatterCurrency, formatterPercentage } from '@/utilities/format';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import { cn } from '@simplywallst/ui-core/next';
import { formatDate } from 'date-fns';
import { useCallback, useState, type ReactNode } from 'react';
import type { Narrative } from '@/pages/Narrative/hooks/useNarrative';
import { CompanyIntrinsicDiscount } from '@/components/CompanyIntrinsicDiscount/CompanyIntrinsicDiscount';

const CHART_HEIGHT = 68;

const PositiveNegativeValue = ({
  value,
  children,
}: {
  value: number | null;
  children: ReactNode;
}) => (
  <span
    className={cn(
      'text-xs',
      value === 0 || value === null
        ? 'text-legacy-mediumgray'
        : value > 0
          ? 'text-good'
          : 'text-bad'
    )}
  >
    {children}
  </span>
);

export const FairValueHistoryGraph = ({
  narrative,
}: {
  narrative: Narrative;
}) => {
  const companyCurrencyISO =
    getTradingItemCurrencyISO(narrative.company?.data.currencyInfo) ?? 'XXX';

  const [selectedTime, setSelectedTime] = useState<number | null>(null);
  const [selectedPrice, setSelectedPrice] = useState<number | null>(null);
  const [selectedFairValue, setSelectedFairValue] = useState<number | null>(
    null
  );

  const onHover = useCallback(({ time, value, fairValue }) => {
    setSelectedTime(time);
    setSelectedPrice(value);
    setSelectedFairValue(fairValue);
  }, []);

  const fairValue = selectedFairValue ?? narrative.valuation?.fairValue ?? null;
  const valuations = narrative?.valuations ?? [];
  const updatedAt =
    selectedTime ?? narrative.updatedAt ?? new Date().toISOString();
  const lastSharePrice =
    selectedPrice ?? narrative.company?.analysisValue?.lastSharePrice ?? null;
  const return1yAbs = narrative.company?.analysisValue?.return1yAbs ?? null;
  const return7d = narrative.company?.analysisValue?.return7d ?? null;
  const npvPerShare = narrative.company?.analysisValue?.npvPerShare ?? null;

  return (
    <div className="border h-x20 rounded-md border-subtle bg-surface-1 opacity-70 shadow light:shadow-sm lg:h-[200px]">
      <div className="h-x20 p-3 lg:h-[200px] lg:px-4 lg:py-5">
        <MiniPriceChartDefinitions />
        <div className="grid grid-cols-[1fr_.9fr] gap-2 lg:mb-2 lg:grid-cols-[1fr_1.5fr]">
          <div className="grid grid-cols-1 gap-1">
            <div className="flex items-center gap-1 text-xs text-soft">
              <div className="size-1.5 rounded-full bg-chart-03" />
              <div className="flex-1">
                {narrative.owner.displayName || 'User'}&apos;s Fair Value
              </div>
            </div>
            <div className="flex items-center gap-2 pl-2.5">
              <div className="text-sm text-solid">
                {formatterCurrency(fairValue, companyCurrencyISO)}
              </div>
              <CompanyIntrinsicDiscount
                npvPerShare={npvPerShare}
                lastSharePrice={lastSharePrice}
                fairValue={fairValue}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-1">
            <div className="flex items-center gap-1 text-xs text-soft">
              <div className="size-1.5 rounded-full bg-good" />
              <div className="flex-1">
                {formatDate(new Date(updatedAt), 'dd MMM')}
              </div>
            </div>
            <div className="flex items-center pl-2.5">
              <div className="text-sm text-solid">
                {formatterCurrency(lastSharePrice, companyCurrencyISO)}
              </div>
            </div>
          </div>
        </div>
        <NarrativeMiniPriceChart
          companyId={narrative.companyId}
          period={CURRENT_PERIOD}
          height={CHART_HEIGHT}
          interactive={true}
          valuations={valuations}
          onHover={onHover}
          currencyISO={companyCurrencyISO}
        />
        <div className="mt-2 grid grid-cols-[1fr_5fr] gap-2">
          <div className="flex items-center gap-2">
            <div className="text-xs text-soft">1Y</div>
            <PositiveNegativeValue value={return1yAbs}>
              {formatterPercentage(return1yAbs)}
            </PositiveNegativeValue>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-xs text-soft">7D</div>
            <PositiveNegativeValue value={return7d}>
              {formatterPercentage(return7d)}
            </PositiveNegativeValue>
          </div>
        </div>
      </div>
    </div>
  );
};
